import { formatCategory, consoleDebug, formatCentsToDollars } from './util';

/**
 * Track e-commerce event in GTM.
 *
 * @param {string} event - Type of event to push to datalayer (impression, add_to_cart, purchase).
 * @param {object} data - Product data (array of products or single product).
 * @returns {Promise}
 */
export const gtmTrackEvent = (event, data, selectedVariant = null, quantity = null, specialData = null, checkoutUrl = null, isTerpli = null) => {
    return new Promise((resolve) => {
        // Start a collapsed group for this GTM event
        consoleDebug(`🔍 GTM Event: ${event}`, '');
        
        // Improved logging for better object inspection
        consoleDebug('Input Data:', 'event:', event);
        consoleDebug('Input Data:', 'data:', JSON.parse(JSON.stringify(data))); // Creates a clean copy
        consoleDebug('Input Data:', 'selectedVariant:', selectedVariant);
        consoleDebug('Input Data:', 'quantity:', quantity);
        consoleDebug('Input Data:', 'specialData:', specialData);
        
        // Specifically for debugging checkout data
        if (['begin_checkout', 'view_cart'].includes(event)) {
            consoleDebug('Cart Price Summary:', data?.priceSummary);
            consoleDebug('First cart item example:', data?.items?.[0]);
        }

        if (!window.dataLayer) {
            window.dataLayer = [];
            consoleDebug('⚠️ DataLayer initialized - was not previously present');
        }

        let analyticsItems = [];
        let totalValue = 0;

        const roundToTwoDecimals = (num) => Math.round(num * 100) / 100;

        // Helper function to convert cents to dollars (consistent with Surfside)
        const centsToDollars = (cents) => roundToTwoDecimals(cents / 100);

        const getVariantPrice = (variant) => {
            // Variant prices are in dollars, return the appropriate price
            return variant.specialPriceRec && variant.specialPriceRec < variant.priceRec
                ? roundToTwoDecimals(variant.specialPriceRec)
                : roundToTwoDecimals(variant.priceRec);
        };

        // Prepare analyticsItems and totalValue based on the event type
        if (['view_cart', 'begin_checkout', 'purchase'].includes(event)) {
            if (Array.isArray(data.items)) {
                consoleDebug('📦 Processing cart items:', data.items.length);
                analyticsItems = data.items.map((product, index) => {
                    const productData = product.product;
                    const productOption = product.option;
                    const selectedVariant = productData.variants.find(variant => variant.option === productOption);
                    
                    let finalPrice;
                    let discount = 0;
                    
                    if (event === 'purchase') {
                        // For purchase events, use the actual price paid from order item (convert from cents to dollars)
                        finalPrice = centsToDollars(product.price);
                        
                        // Calculate discount based on variant prices (already in dollars)
                        const regularPrice = selectedVariant.priceRec;
                        if (selectedVariant.specialPriceRec && selectedVariant.specialPriceRec < selectedVariant.priceRec) {
                            discount = roundToTwoDecimals(regularPrice - selectedVariant.specialPriceRec);
                        }
                    } else {
                        // For cart/checkout events, use variant prices (already in dollars)
                        finalPrice = getVariantPrice(selectedVariant);
                        discount = selectedVariant.specialPriceRec ? 
                            roundToTwoDecimals(selectedVariant.priceRec - finalPrice) : 0;
                    }
                    
                    const itemSpecialData = product.special || specialData;

                    const item = {
                        "item_id": productData.id || "",
                        "item_name": productData.name || "",
                        "item_category": formatCategory(productData.category) || "",
                        "item_category_2": productData.subcategory || "DEFAULT",
                        "item_variant": selectedVariant?.option || "",
                        "item_brand": productData.brand?.name || "",
                        "quantity": product.quantity || 1,
                        "price": finalPrice || 0,
                        "discount": discount || 0,
                        "coupon": itemSpecialData?.name || "",
                        "index": index // Add position for GA4
                    };
                    
                    // Ensure all required fields are present and not empty
                    if (!item.item_id || !item.item_name) {
                        console.error('Critical item data missing for GA4:', {
                            item_id: item.item_id,
                            item_name: item.item_name,
                            productData: productData
                        });
                    }

                    if (itemSpecialData) {
                        item.promotion_id = itemSpecialData.id;
                        item.promotion_name = itemSpecialData.name;
                        item.creative_name = itemSpecialData.menuDisplayConfiguration?.image || "";
                        item.creative_slot = "product_page";
                        consoleDebug('🏷️ Special/Promotion data added for item:', {
                            itemId: item.item_id,
                            promotionId: item.promotion_id,
                            promotionName: item.promotion_name
                        });
                    }

                    return item;
                });
                
                // Enhanced logging for purchase events
                if (event === 'purchase') {
                    consoleDebug('🛍️ Purchase Event Item Analysis:', {
                        totalItems: analyticsItems.length,
                        items: analyticsItems.map(item => ({
                            id: item.item_id,
                            name: item.item_name,
                            price: item.price,
                            quantity: item.quantity,
                            coupon: item.coupon,
                            hasPromotion: !!(item.promotion_id)
                        }))
                    });
                }
                
                // Calculate total value
                if (event === 'purchase') {
                    // For purchase events, order total is already in dollars
                    totalValue = roundToTwoDecimals(data.total);
                    consoleDebug('💰 Purchase total value (dollars):', totalValue);
                } else if (data.priceSummary) {
                    // For cart and checkout, convert priceSummary.total from cents to dollars
                    totalValue = centsToDollars(data.priceSummary.total);
                    consoleDebug('💰 Cart total value (dollars):', totalValue);
                }
            }
        } else if (['add_to_cart', 'remove_from_cart', 'view_item'].includes(event)) {
            // Process single item actions - variant prices are in dollars
            const finalPrice = getVariantPrice(selectedVariant);
            const discount = selectedVariant.specialPriceRec ? 
                roundToTwoDecimals(selectedVariant.priceRec - finalPrice) : 0;
            
            const item = {
                "item_id": data.id || "",
                "item_name": data.name || "",
                "item_category": formatCategory(data.category) || "",
                "item_category_2": data.subcategory || "DEFAULT",
                "item_variant": selectedVariant?.option || "",
                "item_brand": data.brand?.name || "",
                "quantity": quantity || 1,
                "price": finalPrice || 0,
                "discount": discount || 0,
                "coupon": specialData?.name || ""
            };
            
            // Ensure critical fields are present
            if (!item.item_id || !item.item_name) {
                console.error('Critical item data missing for single item GA4 event:', {
                    item_id: item.item_id,
                    item_name: item.item_name,
                    event: event
                });
            }

            if (specialData) {
                item.promotion_id = specialData.id;
                item.promotion_name = specialData.name;
                item.creative_name = specialData.menuDisplayConfiguration?.image || "";
                item.creative_slot = "product_page";
                consoleDebug('🏷️ Special/Promotion data added:', {
                    promotionId: item.promotion_id,
                    promotionName: item.promotion_name
                });
            }

            analyticsItems.push(item);
            consoleDebug('📝 Single item processed:', {
                id: item.item_id,
                name: item.item_name,
                price: item.price,
                quantity: item.quantity
            });
        }

        if (analyticsItems.length > 0) {
            const dataLayerObject = {
                "event": event,
                "ecommerce": {
                    "currency": "USD",
                    "items": analyticsItems,
                },
            };

            if (event === 'purchase') {
                // Calculate global coupon from items - use the most common coupon
                const itemCoupons = analyticsItems
                    .map(item => item.coupon)
                    .filter(coupon => coupon && coupon !== "");
                
                const globalCoupon = itemCoupons.length > 0 ? itemCoupons[0] : "";
                
                // Enhanced purchase event data consistent with Surfside
                dataLayerObject.ecommerce = {
                    ...dataLayerObject.ecommerce,
                    "transaction_id": data.orderNumber,
                    "value": totalValue,
                    "tax": roundToTwoDecimals(data.tax || 0), // Tax is already in dollars
                    "shipping": roundToTwoDecimals(data.shipping || 0), // Shipping is already in dollars
                    "coupon": globalCoupon, // Use derived coupon from items
                    "affiliation": data.dispensaryName || "Online Store" // Add affiliation like Surfside
                };
                
                // Add user data for enhanced ecommerce
                dataLayerObject.user_data = {
                    "customer_id": data.customerId || data.customer?.id,
                    "phone_number": data.customer?.phone,
                };
                
                // Add order metadata
                dataLayerObject.order_type = data.pickup ? "pickup" : "delivery";
                dataLayerObject.payment_type = data.paymentMethod;
                
                consoleDebug('🛍️ Purchase event details:', {
                    transactionId: data.orderNumber,
                    value: totalValue,
                    tax: dataLayerObject.ecommerce.tax,
                    shipping: dataLayerObject.ecommerce.shipping,
                    orderType: dataLayerObject.order_type,
                    paymentType: dataLayerObject.payment_type,
                    affiliation: dataLayerObject.ecommerce.affiliation
                });
            } else if (['view_cart', 'begin_checkout'].includes(event)) {
                dataLayerObject.ecommerce.value = totalValue;
                
                // Add checkout step information
                if (event === 'begin_checkout') {
                    dataLayerObject.ecommerce.checkout_step = 1;
                    dataLayerObject.ecommerce.checkout_option = "checkout_initiated";
                }
            }

            consoleDebug('📊 Final DataLayer object prepared:', dataLayerObject);

            const pushToDataLayer = () => {
                // Clear previous ecommerce data first
                window.dataLayer.push({ ecommerce: null });
                window.dataLayer.push(dataLayerObject);
                consoleDebug('✅ Event successfully pushed to DataLayer');
            };

            if (event === 'begin_checkout') {
                const checkGtagAvailability = () => {
                    return new Promise((resolveGtag) => {
                        if (typeof window.gtag === 'function') {
                            resolveGtag(true);
                        } else {
                            resolveGtag(false);
                        }
                    });
                };
    
                Promise.race([
                    checkGtagAvailability(),
                    new Promise(resolveTimeout => setTimeout(() => resolveTimeout(false), 3000))
                ]).then((isGtagAvailable) => {
                    if (isGtagAvailable) {
                        consoleDebug('✅ Using gtag for checkout event');
                        window.gtag('event', dataLayerObject.event, {
                            ...dataLayerObject.ecommerce,
                            'event_callback': function () {
                                consoleDebug('✅ Checkout event sent to Google Analytics');
                                resolve();
                            },
                            'event_timeout': isTerpli ? 2000 : 0
                        });
                    } else {
                        consoleDebug('⚠️ gtag not available, falling back to dataLayer.push');
                        pushToDataLayer();
                        resolve();
                    }
                });
            } else if (event === 'purchase') {
                // Remove the 2-second delay for purchase events to match Surfside behavior
                // Both Surfside and GA tracking should happen immediately
                pushToDataLayer();
                consoleDebug('✅ Purchase event sent immediately');
                resolve();
            } else {
                // All other events push immediately
                pushToDataLayer();
                resolve();
            }
        } else {
            consoleDebug('ℹ️ No analytics items to push');
            resolve();
        }
    });
};

/**
 * Track e-commerce actions with addProduct.
 * setCommerceAction types: add, remove, detail
 *
 * @param {string} type - Type of setCommerceAction.
 * @param {object} product - Product object.
 * @param {object} selectedVariant - Variant object.
 *
 * @returns {void}
 */
export function trackEcommerceEvent(type, product, selectedVariant, quantity = null) {
    // Variant prices are in dollars - no conversion needed
    const trackingData = {
        id: product.id || null,                       // Product ID
        name: product.name || null,                   // Product name
        list: null,                           // Product list
        brand: product.brand?.name || null,            // Product brand
        category: product.category || null,           // Product category
        variant: selectedVariant.option || null,      // Product variant
        price: selectedVariant.priceRec || null,     // Product price (already in dollars)
        quantity: quantity || null,                   // Product quantity
        coupon: null,                         // Product coupon
        position: null,                       // Product position
        currency: 'USD'                       // Product currency
    }
}

/**
 * Track purchase event in Sentry/internally.
 *
 * @param {string} orderNumber - Order number.
 * @param {string|number} revenue - Total purchase revenue (already in dollars).
 * @param {object} products - Products in the order.
 *
 * @returns {void}
 */
export function trackEcommercePurchase(orderNumber, revenue, products) {
    const roundToTwoDecimals = (num) => Math.round(num * 100) / 100;
    const centsToDollars = (cents) => roundToTwoDecimals(cents / 100);

    /**
     * Track the transaction.
     */
    const checkoutData = {
        id: orderNumber || null,             // Transaction ID
        affiliation: null,                   // Transaction Affiliation
        revenue: roundToTwoDecimals(revenue), // Revenue is already in dollars
        tax: null,                          // Transaction Tax
        shipping: null,                     // Transaction Shipping
        coupon: null,                       // Transaction Coupon
        list: null,                         // Transaction List
        step: null,                         // Transaction Step
        option: null,                       // Transaction Option
        currency: 'USD',                    // Transaction Currency
    }

    /**
     * Track the products.
     */
    if (Array.isArray(products)) {
        for (let product of products) {
            const productData = product.product;
            const productOption = product.option;
            
            if (!productData || !productData.variants) {
                console.error('Invalid product data format in order:', product);
                continue;
            }
            
            const selectedVariant = productData.variants.find(variant => variant.option === productOption);
            
            if (!selectedVariant) {
                console.error('Selected variant not found for product:', productData?.id);
                continue;
            }
            
            // For purchase tracking, use the actual price paid (convert from cents to dollars)
            const priceInDollars = centsToDollars(product.price);

            const trackingData = {
                id: productData.id || null,                   // Product ID
                name: productData.name || null,               // Product name
                list: null,                           // Product list
                brand: productData.brand?.name || null,       // Product brand
                category: productData.category || null,       // Product category
                variant: selectedVariant?.option || null,     // Product variant
                price: priceInDollars,                       // Product price in dollars
                quantity: product.quantity || null,       // Product quantity
                coupon: null,                         // Product coupon
                position: null,                       // Product position
                currency: 'USD'                       // Product currency
            }
        }
    }
}

/**
 * Process cart items and specials data to match specials with cart items.
 * 
 * @param {Array} cartItems - An array of cart items to match with specials.
 * @param {Array} specialsList - The list of specials from the API.
 * @param {Object} menuData - The menu data containing specialProductsIds.
 * @returns {Object} An object mapping product IDs to their special data.
 */
export const processCartSpecials = (cartItems, specialsList, menuData) => {
    const specials = {};
    if (cartItems && Array.isArray(specialsList) && menuData?.specialProductsIds) {
        cartItems.forEach(item => {
            const foundSpecialId = Object.entries(menuData.specialProductsIds).find(
                ([_, productIds]) => Array.isArray(productIds) && productIds.includes(item.productId)
            );
            if (foundSpecialId) {
                const specialId = foundSpecialId[0];
                const special = specialsList.find(s => s.id === specialId);
                if (special) {
                    specials[item.productId] = special;
                }
            }
        });
    }
    return specials;
};
